// Pure interpretation + selection helpers for the admin publish console.
// Dark-safe response conventions, with the TWO helper shapes kept straight
// (they differ — see apiService.js): doGetCall resolves the data object on
// 200 or { status, errMsg } on non-200; doUpdateCall resolves the data
// object on 200/201 or { errStatus, errMsg } on non-200, and a network
// reject becomes { errStatus: "500" }. The publish surfaces are
// master-flag + type-flag gated, so a 404 means "not enabled" — dark
// state, never an error (this matters most on the retract path, which is
// the incident rollback valve).

export function pendingResult(res) {
  if (res && res.status === 404) {
    return { state: "dark" };
  }
  if (!res || res.status || typeof res !== "object" || !Array.isArray(res.items)) {
    return { state: "error" };
  }
  return { state: "ok", data: res };
}

// A valid publish response always carries publishBatchId (server generates it
// even for an all-skipped batch). 404 = flag off (dark), else error.
export function publishOutcome(res) {
  if (res && res.errStatus) {
    return String(res.errStatus) === "404" ? { state: "dark" } : { state: "error" };
  }
  if (!res || typeof res !== "object" || !("publishBatchId" in res)) {
    return { state: "error" };
  }
  return {
    state: "ok",
    batchId: res.publishBatchId,
    published: res.published || [],
    skipped: res.skipped || [],
  };
}

export function retractOutcome(res) {
  if (res && res.errStatus) {
    return String(res.errStatus) === "404" ? { state: "dark" } : { state: "error" };
  }
  if (!res || typeof res !== "object" || !Array.isArray(res.retracted)) {
    return { state: "error" };
  }
  return { state: "ok", retracted: res.retracted };
}

// Skip-reason table rows: one row per reason, ids listed (audit P0-2: the skip
// reasons existed server-side but were invisible to a human).
export function groupSkipsByReason(skipped) {
  const byReason = new Map();
  (skipped || []).forEach((skip) => {
    const reason = skip && skip.reason ? skip.reason : "UNKNOWN";
    if (!byReason.has(reason)) {
      byReason.set(reason, []);
    }
    byReason.get(reason).push(skip.id);
  });
  return Array.from(byReason.entries()).map(([reason, ids]) => ({ reason, ids }));
}

// What each server skip reason means for the operator (kept next to the grouping
// so the table never shows a bare enum).
export const SKIP_REASON_HELP = {
  NOT_FOUND: "Item no longer exists (or was deactivated).",
  TYPE_DISABLED: "This review type is dark — flag it on before publishing.",
  NOT_APPROVED: "Item is not APPROVED yet — publishing requires an approved verdict.",
  ALREADY_PUBLISHED: "Already published (or lost a concurrent race) — nothing re-run.",
  NOT_UNPUBLISHED: "Item was retracted — a retracted item never silently re-publishes.",
  SIDE_EFFECT_FAILED: "The CDN copy failed — item left UNPUBLISHED, safe to retry.",
  DANGER_FLAGGED: "A reviewer flagged this image as a clinical-safety concern — not published without an explicit, audited override.",
  STEM_ATTESTATION_REQUIRED: "This image is placed WITH the question (stem) — publishing requires an explicit, audited attestation that it does not reveal the answer.",
  UNKNOWN: "Unrecognized skip reason — check server logs.",
};

// --- S1.2 / F-S5-1: per-item STEM attestation + danger override (the P0-2 dead-end closer) ---

// Only an EXPLICIT STEM placement is STEM (trim + case-insensitive — mirrors the server's
// ImagePlacement.isStem and the reviewer queue's candidatePlacement leak-safe default).
export function isStemPlaced(item) {
  return String((item && item.payload && item.payload.placement) || "").trim().toUpperCase() === "STEM";
}

// The admin aggregate danger flag (S4): true only when the server says a reviewer flagged it.
export function isDangerFlagged(item) {
  return Boolean(item && item.dangerFlagged === true);
}

// Build the publish-request args (F-S5-1): attested/override ids are intersected with the
// SELECTION (an id checked but not selected never rides the request), and the extras object
// is null when nothing applies so the plain publish path stays byte-identical to before.
// Per-item lists ONLY — batch-level booleans are banned (OVERRIDE_BOOLEAN_RETIRED class).
export function buildPublishRequest(selectedIds, attestedIds, overrideIds, overrideReason) {
  const sel = new Set(selectedIds || []);
  const attested = (attestedIds || []).filter((id) => sel.has(id));
  const overrides = (overrideIds || []).filter((id) => sel.has(id));
  if (attested.length === 0 && overrides.length === 0) {
    return { itemIds: selectedIds || [], extras: null };
  }
  const extras = {};
  if (attested.length > 0) extras.stemLeakageAttestedItemIds = attested;
  if (overrides.length > 0) {
    extras.overrideItemIds = overrides;
    extras.overrideReason = String(overrideReason || "").trim();
  }
  return { itemIds: selectedIds || [], extras };
}

// The fail-closed reason gate: a SELECTED override with a blank reason blocks publish
// client-side (mirrors the server's 400 — the reason is REQUIRED in the audit row).
export function overrideReasonMissing(selectedIds, overrideIds, overrideReason) {
  const sel = new Set(selectedIds || []);
  const active = (overrideIds || []).some((id) => sel.has(id));
  return active && String(overrideReason || "").trim() === "";
}

// Selection helpers (array-based so React state stays a plain array).
export function toggleId(selected, id) {
  return selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id];
}

export function selectableIds(items) {
  return (items || []).map((item) => item.id);
}

// Summary for the publish confirm gate (E2): how many items, and which distinct
// questions, the selected ids touch — so the dialog states WHAT goes live to
// students ("Publish N images across M questions") instead of a generic prompt.
// Order follows the pending worklist (stable, matches what the operator sees).
export function summarizeSelection(pending, selectedIds) {
  const ids = new Set(selectedIds || []);
  const items = (pending || []).filter((it) => it && ids.has(it.id));
  const subjectKeys = [];
  const seen = new Set();
  items.forEach((it) => {
    const key = it.subjectKey;
    if (key != null && key !== "" && !seen.has(key)) {
      seen.add(key);
      subjectKeys.push(key);
    }
  });
  return { count: items.length, subjectKeys };
}

// A batch id is a UUID; the manual retract field accepts nothing else.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
export function isValidBatchId(value) {
  return UUID_RE.test((value || "").trim());
}
